import { Middleware } from 'redux'
import { AppDispatch, AppRootState, AppThunk } from './appStore'
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { DashboardSelectionType, exportCancelReasons, exportToDisplayExport, isDashboardSelectionMatch, statusedWorkItemKey, WorkItemType } from '@fusion-advantage/formlib'
import { DashboardMwResultSliceState, setDashboardMwResult } from './dashboardMwResultSlice'
import { dashboardDataReloadFromLdb, selectDashboardDisplayIdentity } from './dashboardDataSlice'
import { appError } from './errorSlice'
import { mustSelectExport } from '../dal2/selectExport'
import { resetDashboardSelection, setDashboardVideoBobJobberOpen } from './dashboardControlSlice'
import { ApptEncounterGuidSelector } from '../dal2/ApptEncounterGuidSelector'
import { err, ok, Result } from 'neverthrow'

export const dashboardMw: Middleware<{}, AppRootState> = store => {
	const dispatch = store.dispatch as AppDispatch
	return next => action => {
		const prevState = store.getState()
		const result = next(action)
		const nextState = store.getState()
		// sync has ended
		if(prevState.sync.endedAt === null && nextState.sync.endedAt !== null) {
			dispatch(dashboardDataReloadFromLdb())
		}
		if(nextState.dashboardData.authedIdentity?.upn !== prevState.dashboardData.authedIdentity?.upn) {
			dispatch(resetDashboardSelection())
		}
		const selectionOrWorkItemsChanged = (
			(nextState.dashboardControl.selection !== prevState.dashboardControl.selection) ||
			(nextState.dashboardData.workItems !== prevState.dashboardData.workItems)
		)
		if(selectionOrWorkItemsChanged) {
			if(shouldResetSelection(nextState)) {
				dispatch(resetDashboardSelection())
			} else {
				dispatch(updateDashboardDetailResult())
			}
		}
		if(
			nextState.dashboardControl.videoBobJobberOpen &&
			(
				selectionOrWorkItemsChanged ||
				(nextState.app.backgroundTaps > prevState.app.backgroundTaps)
			)
		) {
			// console.debug('BONG!', nextState.app.backgroundTaps)
			dispatch(setDashboardVideoBobJobberOpen(false))
		}
		return result
	}
}

const shouldResetSelection = (nextState: AppRootState): boolean => {
	const selection = nextState.dashboardControl.selection
	switch(selection.type) {
		case DashboardSelectionType.TodaySummary:
			return false
		case DashboardSelectionType.WorkItem:
			const items = nextState.dashboardData.workItems[selection.key.type].items ?? []
			const item = items.find((statusedItem) => isDashboardSelectionMatch(statusedWorkItemKey(statusedItem), selection))
			return item ? false : true
	}
}

const updateDashboardDetailResult = (): AppThunk<Promise<void>> => async (dispatch, getState) => {
	const r = await (async(): Promise<Result<DashboardMwResultSliceState|null, FusionError>> => {
		const selection = getState().dashboardControl.selection
		switch(selection.type) {
			case DashboardSelectionType.TodaySummary:
				return ok(null)
			case DashboardSelectionType.WorkItem:
				const upn = selectDashboardDisplayIdentity(getState())?.upn
				if(!upn) {
					return err(serializeFusionError('updateDashboardDetailResult', 'no user identity available'))
				}
				let encounterGuid: string
				switch(selection.key.type) {
					case WorkItemType.Todo:
						encounterGuid = selection.key.encounterGuid	
						break
					case WorkItemType.Appointment:
						const apptEncSelector = await ApptEncounterGuidSelector.create(upn)
						const rEncounterGuid = apptEncSelector.mustGet(selection.key.serviceApptId)
						if(rEncounterGuid.isErr()) {
							return err(serializeFusionError('updateDashboardDetailResult', rEncounterGuid.error))
						}
						encounterGuid = rEncounterGuid.value
						break
				}
				const rSelectExp = await mustSelectExport(upn, encounterGuid)
				if(rSelectExp.isErr()) {
					return err(serializeFusionError('updateDashboardDetailResult', rSelectExp.error))
				}
				return ok({
					error: null,
					displayExp: exportToDisplayExport(rSelectExp.value.exp),
					pendingSync: rSelectExp.value.pendingSync,
					cancelReasons: exportCancelReasons(rSelectExp.value.exp),
				})
		}
	})()
	if(r.isErr()) {
		dispatch(appError({
			err: r.error,
			suppressAlert: true,
		}))
		dispatch(setDashboardMwResult({
			error: r.error,
			displayExp: null,
			pendingSync: false,
			cancelReasons: [],
		}))
		return
	}
	dispatch(setDashboardMwResult(r.value))
}
